import React, { useMemo, useState } from "react";
import { FONTS, getStyle } from "./studioModel";

export function documentFonts(doc) {
  const fonts = new Map();
  const add = (value, used = false) => {
    if (!value) return;
    const label = value.split(",")[0].trim().replace(/^["']|["']$/g, "");
    const key = label.toLowerCase();
    const old = fonts.get(key);
    fonts.set(key, { label, value: old?.value || value, count: (old?.count || 0) + Number(used) });
  };
  doc.pages.forEach((p) => p.elements.filter((n) => n.type === "text").forEach((n) => add(getStyle(doc,n).fontFamily, true)));
  Object.values(doc.styles).forEach((s) => add(s.fontFamily));
  (doc.fonts || []).forEach((f) => add(f.family));
  return [...fonts.values()].sort((a,b) => a.label.localeCompare(b.label));
}

export default function StudioFontPicker({ doc, value, onChange }) {
  const [search, setSearch] = useState("");
  const [desktop, setDesktop] = useState([]);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const used = useMemo(() => documentFonts(doc), [doc]);
  const label = (value || "Choose font").split(",")[0].replace(/["']/g, "").trim();
  const scan = async () => {
    setBusy(true); setError("");
    try {
      const fonts = await window.queryLocalFonts();
      setDesktop([...new Set(fonts.map((f) => f.family))].sort());
      if (!fonts.length) setError("No desktop fonts were shared. Try allowing access again.");
    } catch (_) { setError("Font access wasn’t granted. You can try again or enter a font name below."); }
    finally { setBusy(false); }
  };
  const addLocal = async () => {
    const family = name.trim();
    if (!family) return;
    setBusy(true); setError("");
    try {
      const face = new FontFace(family, `local(${JSON.stringify(family)})`);
      await face.load();
      setDesktop((fonts) => [...new Set([...fonts, family])].sort());
      setName("");
    } catch (_) { setError("That font isn’t available to this browser. Check its exact installed name."); }
    finally { setBusy(false); }
  };
  const rows = (fonts) => fonts.filter((f) => f.label.toLowerCase().includes(search.toLowerCase())).map((font) => (
    <button type="button" key={font.label} aria-pressed={label.toLowerCase() === font.label.toLowerCase()}
      className="ms-font-option" onClick={() => onChange(font.value)}>
      <span style={{ fontFamily: font.value }}>{font.label}</span>
      {font.count > 0 && <small>{font.count}</small>}
    </button>
  ));
  return <div className="ms-font-picker">
    <span className="ms-font-label">Font family</span>
    <details>
      <summary aria-label={`Font family: ${label}`}><span style={{ fontFamily: value }}>{label}</span><span aria-hidden="true">⌄</span></summary>
      <div className="ms-font-browser">
        <input aria-label="Search fonts" placeholder="Search fonts…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <div className="ms-font-results">
          <h4>In this document</h4>
          {rows(used)}
          <h4>Standard fonts</h4>
          {rows(FONTS.filter((f) => !used.some((u) => u.label.toLowerCase() === f.toLowerCase())).map((f) => ({label:f,value:f})))}
          <h4>Desktop fonts</h4>
          {rows(desktop.map((f) => ({ label:f, value:JSON.stringify(f) })))}
          {!used.some((f) => f.label.toLowerCase().includes(search.toLowerCase())) && ![...FONTS,...desktop].some((f) => f.toLowerCase().includes(search.toLowerCase())) && <p className="ms-hint">No matching fonts.</p>}
        </div>
        <div className="ms-font-desktop">
          {typeof window.queryLocalFonts === "function" ? <button type="button" disabled={busy} onClick={scan}>{busy ? "Loading fonts…" : "Browse desktop fonts"}</button> : <p className="ms-hint">This browser can’t list desktop fonts. Add an installed font by its exact name.</p>}
          <div className="ms-font-add"><input aria-label="Installed font name" placeholder="Installed font name" maxLength={90} value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if(e.key === "Enter") { e.preventDefault(); addLocal(); } }} /><button type="button" disabled={busy || !name.trim()} onClick={addLocal}>Add</button></div>
          <p className="ms-hint">Desktop fonts must also be installed on other computers that open this design.</p>
          {error && <p role="alert" className="ms-hint">{error}</p>}
        </div>
      </div>
    </details>
  </div>;
}
